import baseApi from '@/app/store/api/baseApi';
import { API_URLS } from '@/config/apiUrls';
import { ssoConfig } from '@/features/login/constants';
import {
    authResponseHandler,
    transformAuthError,
    unwrapAuthRequest,
} from '@/services/login/auth/authRequest';

import type { AppDispatch } from '@/app/store/store';
import type { UmexRole } from '@/features/servicePointSelection/utils/servicePointUtils';

interface AuthEnvelope<Result> {
    status: string;
    data: Result[];
    timestamp?: string;
}

interface SsoUser {
    id?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    language?: string;
    roles?: { roles: UmexRole[] };
}

export interface SsoCallbackRequest {
    code: string;
    state: string | null;
    redirectUri: string;
}

export interface SsoCallbackResponse extends AuthEnvelope<{ user: SsoUser; token: string }> {
    error?: { code?: string | number; msg?: string; details?: unknown };
    umexCode?: string;
}

export const authApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // These GETs are imperative login commands, not shared cached user data.
        authRedirectUrl: builder.mutation<AuthEnvelope<{ redirectUrl: string }>, void>({
            query: () => ({
                url: ssoConfig.redirectUrl,
                method: 'GET',
                responseHandler: authResponseHandler,
            }),
            extraOptions: { skipAuth: true, skipReauth: true },
            transformErrorResponse: transformAuthError,
        }),
        authCallback: builder.mutation<SsoCallbackResponse, SsoCallbackRequest>({
            query: (body) => ({
                url: ssoConfig.callbackUrl,
                method: 'POST',
                body,
                responseHandler: authResponseHandler,
            }),
            extraOptions: { skipAuth: true, skipReauth: true },
            transformErrorResponse: transformAuthError,
        }),
        authRefresh: builder.mutation<AuthEnvelope<{ token: string }>, { token: string }>({
            query: (body) => ({
                url: ssoConfig.refreshUrl,
                method: 'POST',
                body,
                responseHandler: authResponseHandler,
            }),
            extraOptions: { skipAuth: true, skipReauth: true },
            transformErrorResponse: transformAuthError,
        }),
        authGenerateLoginToken: builder.mutation<string, void>({
            query: () => ({
                url: API_URLS.customer.tokenGenerate,
                method: 'GET',
                responseHandler: authResponseHandler,
            }),
            extraOptions: { skipAuth: true, skipReauth: true },
            transformErrorResponse: transformAuthError,
        }),
    }),
});

export const fetchSsoRedirectUrl = (dispatch: AppDispatch) =>
    unwrapAuthRequest(
        dispatch(authApi.endpoints.authRedirectUrl.initiate(undefined, { track: false })),
    );

export const exchangeSsoCallback = (dispatch: AppDispatch, body: SsoCallbackRequest) =>
    unwrapAuthRequest(dispatch(authApi.endpoints.authCallback.initiate(body, { track: false })));

export const refreshAuthToken = (dispatch: AppDispatch, body: { token: string }) =>
    unwrapAuthRequest(dispatch(authApi.endpoints.authRefresh.initiate(body, { track: false })));

export const generateLoginToken = (dispatch: AppDispatch) =>
    unwrapAuthRequest(
        dispatch(authApi.endpoints.authGenerateLoginToken.initiate(undefined, { track: false })),
    );
